// src/components/BookingHistory.js - Updated with notes display and color coding
import React, { useState, useEffect } from 'react';
import { getAllBookings, deleteBooking } from '../services/bookingService';
import './EnhancedBookingsTable.css';

/**
 * @typedef {Object} ClassTypeStyle
 * @property {string} detailsClass - CSS class for details section
 * @property {string} badgeClass - CSS class for badge
 * @property {string} label - Label text
 */

/**
 * Function to determine class type and return appropriate CSS class
 * @param {string} className - The class name to check
 * @returns {ClassTypeStyle} Object containing CSS classes and label
 */
const getClassTypeStyles = (className) => {
  if (!className) return {
    detailsClass: '',
    badgeClass: '',
    label: ''
  };
  
  const lowerClassName = className.toLowerCase();
  
  if (lowerClassName.includes('cert 3') || lowerClassName.includes('cert3')) {
    return {
      detailsClass: 'cert3-class',
      badgeClass: 'cert3-badge',
      label: 'Cert 3'
    };
  } else if (lowerClassName.includes('cert 4') || lowerClassName.includes('cert4')) {
    return {
      detailsClass: 'cert4-class',
      badgeClass: 'cert4-badge',
      label: 'Cert 4'
    };
  } else if (lowerClassName.includes('diploma')) {
    return {
      detailsClass: 'diploma-class',
      badgeClass: 'diploma-badge',
      label: 'Diploma'
    };
  }
  
  return {
    detailsClass: '',
    badgeClass: '',
    label: ''
  };
};

// Define highlight colors for each class type
const classHighlightColors = {
  'cert3': 'rgba(0, 184, 212, 0.10)', // cyan
  'cert4': 'rgba(61, 124, 245, 0.10)', // blue
  'diploma': 'rgba(255, 193, 7, 0.10)', // yellow
  'default': 'rgba(61,124,245,0.08)'
};

const BookingHistory = ({ equipmentData = [], isStaff = false }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('completed'); // Default to completed bookings
  const [searchQuery, setSearchQuery] = useState('');
  const [activeNotes, setActiveNotes] = useState(null); // State to track which notes popup is active
  const [expandedBookings, setExpandedBookings] = useState({});
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');

  // Create mapping of equipment ID to its category and type
  const equipmentCategories = {};
  
  // Only process equipmentData if it exists and is an array
  if (Array.isArray(equipmentData)) {
    equipmentData.forEach(item => {
      equipmentCategories[item.id] = {
        category: item.category,
        type: item.type
      };
    });
  }

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const result = await getAllBookings();
        if (result.success) {
          // Sort bookings based on current filter
          const sortedBookings = result.bookings.sort((a, b) => {
            const dateA = new Date(a.startDate || a.createdAt || 0).getTime();
            const dateB = new Date(b.startDate || b.createdAt || 0).getTime();
            // For active bookings, sort ascending (oldest first)
            // For completed and all bookings, sort descending (newest first)
            return filter === 'active' ? dateA - dateB : dateB - dateA;
          });
          setBookings(sortedBookings);
        } else {
          setError(result.error || 'Failed to load booking history');
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching booking history');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [filter]); // Add filter as a dependency to re-sort when filter changes

  // Add click handler to close notes popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeNotes && !event.target.closest('.notes-popup') && 
          !event.target.closest('.notes-button')) {
        setActiveNotes(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeNotes]);

  // Toggle notes popup
  const toggleNotes = (bookingId) => {
    if (activeNotes === bookingId) {
      setActiveNotes(null);
    } else {
      setActiveNotes(bookingId);
    }
  };

  // Helper function to check if ALL items in a booking are checked in
  const isBookingFullyCheckedIn = (booking) => {
    try {
      // A booking is fully checked in if all items have been checked in
      if (!booking || !booking.items || !Array.isArray(booking.items) || 
          !booking.checkedInItems) {
        return false;
      }
      
      // First, check if all items are actually checked out
      const allCheckedOut = booking.items.every(item => 
        item && item.equipmentId && 
        booking.checkedOutItems && 
        booking.checkedOutItems[item.equipmentId]
      );
      
      // If not all items are checked out, it can't be fully checked in
      if (!allCheckedOut) return false;
      
      // Now check if all items are checked in
      return booking.items.every(item => 
        item && item.equipmentId && 
        booking.checkedInItems[item.equipmentId]
      );
    } catch (error) {
      return false;
    }
  };

  // Helper function to check if a booking is completed
  const isBookingCompleted = (booking) => {
    if (!booking) return false;
    
    // A booking is completed if:
    // 1. It's explicitly marked as returned
    // 2. It has a returned date
    // 3. All items are checked in (if the booking has items)
    return (
      booking.status === 'Returned' || 
      booking.returnedDate || 
      (booking.items && 
       Array.isArray(booking.items) && 
       booking.items.length > 0 && 
       booking.checkedInItems && 
       booking.items.every(item => 
         item && 
         item.equipmentId && 
         booking.checkedInItems[item.equipmentId]
       ))
    );
  };
  
  // Filter bookings based on current filter and search query with safety checks
  const getFilteredBookings = () => {
    if (!Array.isArray(bookings)) {
      return [];
    }
    
    return bookings.filter(booking => {
      if (!booking) return false;
      
      try {
        // Apply status filter
        if (filter === 'active') {
          // Active bookings are those that are approved but not fully returned/checked in
          return booking.status === 'Approved' && !isBookingCompleted(booking);
        }
        
        if (filter === 'completed') {
          // Completed bookings are those that are either marked as returned or fully checked in
          return booking.status === 'Returned' || isBookingCompleted(booking);
        }
        
        // For 'all' filter, show all bookings regardless of status
        if (filter === 'all') {
          return true;
        }
        
        // Apply search query if present
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return (
            (booking.studentName && booking.studentName.toLowerCase().includes(query)) ||
            (booking.studentId && booking.studentId.toLowerCase().includes(query)) ||
            (booking.productionName && booking.productionName.toLowerCase().includes(query)) ||
            (booking.className && booking.className.toLowerCase().includes(query))
          );
        }
        
        return true;
      } catch (error) {
        console.error('Error filtering booking:', error);
        return false;
      }
    });
  };

  const filteredBookings = getFilteredBookings();

  // Format date to be more readable
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Group booking items by category for display
  const groupBookingItemsByCategory = (bookingItems) => {
    if (!Array.isArray(bookingItems) || bookingItems.length === 0) {
      return {};
    }
    
    try {
      const groupedItems = {};
      
      bookingItems.forEach(item => {
        if (!item || !item.equipmentId) return;
        
        const equipInfo = equipmentCategories[item.equipmentId];
        if (equipInfo) {
          const category = equipInfo.category;
          if (!groupedItems[category]) {
            groupedItems[category] = [];
          }
          groupedItems[category].push({
            ...item,
            category: category,
            type: equipInfo.type
          });
        } else {
          // Fallback for items that don't have category info
          if (!groupedItems['Other']) {
            groupedItems['Other'] = [];
          }
          groupedItems['Other'].push(item);
        }
      });
      
      return groupedItems;
    } catch (error) {
      return { 'Error': bookingItems };
    }
  };

  // Get the appropriate status label for a booking
  const getBookingStatusLabel = (booking) => {
    if (!booking) return 'Unknown';
    
    if (booking.status === 'Returned' || isBookingCompleted(booking)) {
      return 'Returned';
    }
    
    if (booking.status === 'Approved') {
      const hasCheckedOut = booking.checkedOutItems && 
                           Object.keys(booking.checkedOutItems).length > 0;
      const hasCheckedIn = booking.checkedInItems && 
                          Object.keys(booking.checkedInItems).length > 0;
      
      if (hasCheckedOut && hasCheckedIn) {
        return 'Partially Returned';
      } else if (hasCheckedOut) {
        return 'Checked Out';
      }
      return 'Approved';
    }
    
    if (booking.status === 'Pending') {
      return 'Pending Approval';
    }
    
    return booking.status || 'Unknown';
  };

  // Get CSS class for badge based on booking status
  const getStatusBadgeClass = (booking) => {
    if (!booking) return 'pending';
    
    if (booking.status === 'Returned' || isBookingCompleted(booking)) {
      return 'success';
    }
    
    if (booking.status === 'Approved') {
      const hasCheckedOut = booking.checkedOutItems && 
                           Object.keys(booking.checkedOutItems).length > 0;
      const hasCheckedIn = booking.checkedInItems && 
                          Object.keys(booking.checkedInItems).length > 0;
      
      if (hasCheckedOut && hasCheckedIn) {
        return 'warning'; // Partially returned
      } else if (hasCheckedOut) {
        return 'warning'; // Checked out
      }
      return 'success'; // Just approved
    }
    
    return 'pending';
  };

  const toggleBookingExpand = (bookingId) => {
    setExpandedBookings(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  const handleEditNotes = (bookingId, newNotes) => {
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, notes: newNotes } : b));
    setEditingNotesId(null);
    setEditingNotesValue('');
    // Optionally, update notes in the backend here if needed
  };

  // Delete booking handler
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) return;
    await deleteBooking(bookingId);
    // Refresh bookings
    setBookings(prev => prev.filter(b => b.id !== bookingId));
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3>
          📚
          Booking History
        </h3>
        <p>Complete record of all equipment bookings</p>
      </div>
      
      <div className="card-content">
        {/* Filter and Search Controls */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div className="view-mode-tabs">
            <button 
              className={`view-mode-tab ${filter === 'all' ? 'active animate-fade-in' : 'animate-fade-in'}`}
              onClick={() => setFilter('all')}
            >
              All Bookings
            </button>
            <button 
              className={`view-mode-tab ${filter === 'completed' ? 'active animate-fade-in' : 'animate-fade-in'}`}
              onClick={() => setFilter('completed')}
            >
              Completed Bookings
            </button>
          </div>
          
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by student, project or equipment..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                background: 'var(--input-bg)',
                color: 'var(--text-primary)',
                width: '250px'
              }}
            />
          </div>
        </div>
        
        {loading ? (
          <div className="empty-message">Loading booking history...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : !Array.isArray(bookings) ? (
          <div className="empty-message">Unable to load bookings</div>
        ) : filteredBookings.length === 0 ? (
          <div className="empty-message">
            {searchQuery 
              ? 'No bookings match your search criteria'
              : filter === 'completed'
                ? 'No completed bookings available'
                : 'No booking history available'}
          </div>
        ) : (
          <div className="booking-history-list">
            {filteredBookings.map((booking, index) => {
              // Get class type styling
              const classTypeStyle = getClassTypeStyles(booking.className);
              
              const classTypeKey = classTypeStyle.label ? classTypeStyle.label.toLowerCase().replace(/\s/g, '') : 'default';
              const cardBg = classHighlightColors[classTypeKey] || classHighlightColors['default'];

              return (
                <div key={booking.id || index} className="booking-card" style={
                  expandedBookings[booking.id]
                    ? { borderRadius: 12, boxShadow: '0 2px 8px 0 rgba(61,124,245,0.06)' }
                    : { background: cardBg, borderRadius: 12, boxShadow: '0 2px 8px 0 rgba(61,124,245,0.06)' }
                }>
                  <div
                    className="booking-header"
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 24 }}
                    onClick={() => toggleBookingExpand(booking.id)}
                  >
                    <span
                      className="chevron-icon"
                      style={{
                        display: 'inline-flex',
                        transition: 'transform 0.25s cubic-bezier(.4,2,.6,1)',
                        marginRight: 16,
                        fontSize: 0,
                        transform: expandedBookings[booking.id] ? 'rotate(90deg)' : 'rotate(0deg)',
                        color: expandedBookings[booking.id] ? '#3d7cf5' : '#888',
                        filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.08))',
                      }}
                    >
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 6 15 12 9 18" />
                      </svg>
                    </span>
                    <div style={{ minWidth: 200 }}>
                      <div className="student-name" style={{ fontWeight: 700, fontSize: '1.15rem', color: '#fff' }}>{booking.studentName || 'Unknown'}</div>
                      <div className="student-id" style={{ fontSize: '0.95rem', color: '#aaa', marginTop: 2 }}>{booking.studentId || 'No ID'}</div>
                    </div>
                    {/* Project Details Section with dynamic class */}
                    {(booking.productionName || booking.className || booking.projectType) && (
                      <div className={`booking-project-details ${classTypeStyle.detailsClass}`} style={{ flex: 1, margin: '0 16px', display: 'flex', alignItems: 'flex-start', padding: '12px 16px', gap: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          {booking.productionName && (
                            <div className="booking-project-item"><span className="booking-label">Production:</span> <span className="booking-value">{booking.productionName}</span></div>
                          )}
                          {booking.projectType && (
                            <div className="booking-project-item"><span className="booking-label">Project Type:</span> <span className="booking-value">{booking.projectType}</span></div>
                          )}
                        </div>
                        {booking.className && (
                          <div className={`class-badge ${classTypeStyle.badgeClass}`} style={{ alignSelf: 'flex-start', fontSize: '1rem', padding: '6px 14px', borderRadius: 6, fontWeight: 600, marginLeft: 8 }}>{classTypeStyle.label || booking.className}</div>
                        )}
                      </div>
                    )}
                    {/* Move notes section here, after project details and before dates */}
                    {booking.notes && (
                      <div className="notes-indicator-wrapper" style={{ position: 'relative', margin: '8px 0 0 16px', display: 'flex', alignItems: 'center' }}>
                        <button className="notes-button" onClick={() => toggleNotes(booking.id)} title="View student notes">
                          <span className="notes-icon">📝</span>
                          <span className="notes-text">Notes</span>
                        </button>
                        {activeNotes === booking.id && (
                          <div className="notes-popup" style={{ position: 'absolute', left: 0, top: '100%', zIndex: 10, minWidth: 220, marginTop: 8 }}>
                            <div className="notes-popup-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h4 style={{ margin: 0 }}>Student Notes</h4>
                              <button className="close-popup" onClick={() => setActiveNotes(null)} style={{ fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
                            </div>
                            <div className="notes-popup-content">
                              {editingNotesId === booking.id ? (
                                <>
                                  <textarea
                                    value={editingNotesValue}
                                    onChange={e => setEditingNotesValue(e.target.value)}
                                    style={{ width: '100%', minHeight: 60, borderRadius: 4, border: '1px solid #ccc', padding: 8, resize: 'vertical', fontSize: '1rem' }}
                                  />
                                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <button onClick={() => handleEditNotes(booking.id, editingNotesValue)} style={{ padding: '6px 16px', borderRadius: 4, background: '#3d7cf5', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Save</button>
                                    <button onClick={() => { setEditingNotesId(null); setEditingNotesValue(''); }} style={{ padding: '6px 16px', borderRadius: 4, background: '#eee', color: '#333', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{booking.notes}</p>
                                  <button onClick={() => { setEditingNotesId(booking.id); setEditingNotesValue(booking.notes || ''); }} style={{ marginTop: 8, padding: '6px 16px', borderRadius: 4, background: '#3d7cf5', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Dates stacked vertically */}
                    <div className="booking-dates" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 110, gap: 2 }}>
                      <div className="booking-date">
                        <span className="booking-label" style={{ color: '#aaa', fontSize: '0.95rem' }}>Start:</span>
                        <span className="booking-value" style={{ fontWeight: 500, marginLeft: 6 }}>{formatDate(booking.startDate)}</span>
                      </div>
                      <div className="booking-date">
                        <span className="booking-label" style={{ color: '#aaa', fontSize: '0.95rem' }}>Return:</span>
                        <span className="booking-value" style={{ fontWeight: 500, marginLeft: 6 }}>{formatDate(booking.endDate)}</span>
                      </div>
                      {filter !== 'completed' && booking.returnedDate && (
                        <div className="booking-date">
                          <span className="booking-label" style={{ color: '#aaa', fontSize: '0.95rem' }}>Returned:</span>
                          <span className="booking-value" style={{ fontWeight: 500, marginLeft: 6 }}>{formatDate(booking.returnedDate)}</span>
                        </div>
                      )}
                    </div>
                    {/* Status */}
                    <div className="booking-status" style={{ marginLeft: 16, alignSelf: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span className={`badge ${getStatusBadgeClass(booking)}`}>{getBookingStatusLabel(booking)}</span>
                      {filter === 'completed' && booking.returnedDate && (
                        <span style={{ fontSize: '0.85rem', color: '#aaa' }}>
                          {formatDate(booking.returnedDate)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className={`expand-section${expandedBookings[booking.id] ? ' expanded' : ''}`}> 
                    <div
                      style={{
                        padding: expandedBookings[booking.id] ? 16 : 0,
                        opacity: expandedBookings[booking.id] ? 1 : 0,
                        transition: 'opacity 0.25s, padding 0.25s',
                        pointerEvents: expandedBookings[booking.id] ? 'auto' : 'none',
                        height: expandedBookings[booking.id] ? 'auto' : 0,
                      }}
                    >
                      <div className="booking-equipment">
                        <h4>Equipment</h4>
                        {!booking.items || !Array.isArray(booking.items) || booking.items.length === 0 ? (
                          <div className="empty-message">No equipment items for this booking</div>
                        ) : (
                          <div className="equipment-by-category">
                            {Object.entries(groupBookingItemsByCategory(booking.items)).map(([category, items], catIndex) => (
                              <div key={`${booking.id}-${category}-${catIndex}`} className="equipment-category">
                                <h5>{category}</h5>
                                <table className="equipment-table">
                                  <thead>
                                    <tr>
                                      <th>Item</th>
                                      <th>Type</th>
                                      <th>Qty</th>
                                      <th>Checked Out</th>
                                      <th>Checked In</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((item, itemIndex) => (
                                      <tr key={`${booking.id}-${item.equipmentId || itemIndex}`}>
                                        <td>{item.equipmentName || 'Unknown Item'}</td>
                                        <td>{item.type || '-'}</td>
                                        <td>{item.quantity || 0}</td>
                                        <td style={{ textAlign: 'center' }}>
                                          {booking.checkedOutItems && item.equipmentId && booking.checkedOutItems[item.equipmentId] ? (
                                            <span style={{ color: 'var(--success-color)' }}>✓</span>
                                          ) : (
                                            <span style={{ color: 'var(--danger-color)' }}>✗</span>
                                          )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                          {booking.checkedInItems && item.equipmentId && booking.checkedInItems[item.equipmentId] ? (
                                            <span style={{ color: 'var(--success-color)' }}>✓</span>
                                          ) : (
                                            <span style={{ color: 'var(--danger-color)' }}>✗</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Delete button for staff only */}
                      {isStaff && (
                        <button
                          className="button danger small"
                          style={{ marginTop: 16 }}
                          onClick={() => handleDeleteBooking(booking.id)}
                        >
                          Delete Booking
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingHistory;